import { buildOrderInfoPath } from "@jasper/product-workflows";
import { ItemType, NEW_PAP_WORKFLOW_ACTIVATION_DATE } from "@jasper/shared";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { FC, JSX } from "react";
import React from "react";
import {
  HashRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { SteppedOrderCreationPage } from "../pages/orders/create/new/SteppedOrderCreationPage";
import { TermsOfSalePage } from "../pages/v2/legal/TermsOfSalePage";
import { AddProduct } from "../pages/v2/orders/configure/AddProduct";
import DefaultProduct from "../pages/v2/orders/configure/DefaultProduct";
import { OrderConfigurationLayout } from "../pages/v2/orders/configure/OrderConfigurationLayout";
import { OrderConfirmation } from "../pages/v2/orders/configure/OrderConfirmation";
import { OrderInfo } from "../pages/v2/orders/configure/OrderInfo";
import ProductConfiguration from "../pages/v2/orders/configure/ProductConfiguration";
import {
  RemoteOrderFilesUpload,
  RemoteUploadFile,
} from "../pages/v2/orders/configure/RemoteUploadFile";
import ResumeOrderConfiguration from "../pages/v2/orders/configure/ResumeOrderConfiguration";
import { OrderSummary } from "../pages/v2/orders/configure/summary/OrderSummary";
import PreferencesPage from "../pages/v2/settings/Preferences";
import { useAuthContext } from "../shared/contexts/AuthContext";
import { useCombinedWorkflowOrderStore } from "../shared/stores/orders/create-combined-workflow-store.store";
import { useCreateOrderStore } from "../shared/stores/orders/create-store.store";
import { LoggedInLayout } from "/@/components/layout/LoggedInLayout";
import { Dashboard } from "/@/pages/dashboard/Dashboard";
import { LoginPage } from "/@/pages/login/LoginPage";
import { NotificationsListPage } from "/@/pages/notifications/NotificationsListPage";
import { CreateOrderFormPage } from "/@/pages/orders/create/CreateOrderFormPage";
import { CreateOrderPage } from "/@/pages/orders/create/CreateOrderPage";
import { OrderPage } from "/@/pages/orders/OrderPage";
import { OrdersListPage } from "/@/pages/orders/OrdersListPage";
import { UploadFileHelpPage } from "/@/pages/orders/UploadFileHelpPage";
import { UploadFilePage } from "/@/pages/orders/UploadFilePage";
import { SettingsPage } from "/@/pages/settings/SettingsPage";
import { CreateOrderFromOrderInfoPage } from "/@/pages/v2/orders/new/CreateOrderFromOrderInfoPage";
import { getWebsocketApiUrl } from "/@/services/api/api";
import { CreateOrderFileInterceptors } from "/@/services/ipc/createOrderFile.interceptor";
import { useIpcContext } from "/@/shared/contexts/IpcContext";

export const RoutesDispatcher: FC = () => {
  const isThemeV2Enabled = useFeatureFlagEnabled("theme_v2");
  return (
    <HashRouter>
      <NewOrderSocketListener />
      <Routes>
        <Route
          path="/order-help-upload-file/:id/:name/:token"
          element={<UploadFileHelpPage />}
        />
        <Route
          path="/order-upload-file/:id/:name/:token/:filetype"
          element={<UploadFilePage />}
        />
        <Route
          path="/login"
          element={
            <CreateOrderRedirect>
              <LoginRedirect>
                <LoginPage />
              </LoginRedirect>
            </CreateOrderRedirect>
          }
        />
        <Route
          path="/create-order"
          element={
            <LoginRequired>
              <CreateOrderPage />
            </LoginRequired>
          }
        />
        <Route
          path="/create-new-order"
          element={
            <LoginRequired>
              <SteppedOrderCreationPage />
            </LoginRequired>
          }
        />
        <Route
          path="/create-order-form"
          element={
            <LoginRequired>
              <CreateOrderFormPage />
            </LoginRequired>
          }
        />
        <Route
          path="/orders"
          element={
            <LoginRequired>
              <CreateOrderRedirect>
                <OrdersListPage />
              </CreateOrderRedirect>
            </LoginRequired>
          }
        />
        <Route
          path="/notifications"
          element={
            <LoginRequired>
              <CreateOrderRedirect>
                <NotificationsListPage />
              </CreateOrderRedirect>
            </LoginRequired>
          }
        />
        <Route
          path="/order/:id"
          element={
            <LoginRequired>
              <CreateOrderRedirect>
                <OrderPage />
              </CreateOrderRedirect>
            </LoginRequired>
          }
        />
        <Route
          path="/settings"
          element={
            <LoginRequired>
              <CreateOrderRedirect>
                {isThemeV2Enabled ? <PreferencesPage /> : <SettingsPage />}
              </CreateOrderRedirect>
            </LoginRequired>
          }
        />
        <Route
          path="/cgv"
          element={
            <LoginRequired>
              <TermsOfSalePage />
            </LoginRequired>
          }
        />

        <Route
          path="/orders/:orderId/remote-upload"
          element={<RemoteUploadFile />}
        />
        <Route
          path="/orders/:orderId/files/remote-upload"
          element={<RemoteOrderFilesUpload />}
        />
        <Route
          path="/orders/:orderId/configure"
          element={
            <LoginRequired>
              <OrderConfigurationLayout />
            </LoginRequired>
          }
        >
          <Route path="order-info" element={<OrderInfo />} />
          <Route path="products/add" element={<AddProduct />} />
          <Route path="products" element={<DefaultProduct />} />
          <Route
            path="products/resume"
            element={<ResumeOrderConfiguration />}
          />
          <Route
            path="products/:productId/:step?"
            element={<ProductConfiguration />}
          />

          <Route path="summary" element={<OrderSummary />} />
          <Route path="confirmation" element={<OrderConfirmation />} />
          <Route path="" element={<Navigate to="order-info" replace />} />
        </Route>

        <Route
          path="/"
          element={
            <LoginRequired>
              <CreateOrderRedirect>
                <Dashboard />
              </CreateOrderRedirect>
            </LoginRequired>
          }
        />
        <Route
          path="/orders/new"
          element={
            <LoginRequired>
              <CreateOrderFromOrderInfoPage />
            </LoginRequired>
          }
        />
      </Routes>
    </HashRouter>
  );
};

const NewOrderSocketListener = () => {
  const { isLoggedIn, user } = useAuthContext();
  const navigate = useNavigate();

  const [resetStore] = useCreateOrderStore(state => [state.resetStore]);

  const [resetCombinedStore, updateCombinedWorkflowProducts] =
    useCombinedWorkflowOrderStore(state => [
      state.resetStore,
      state.updateCombinedWorkflowProducts,
    ]);

  const openNewOrder = (args: {
    orderId?: string;
    productTypes?: string[];
    isOrderFormV2?: boolean;
  }) => {
    if (args?.orderId && args.isOrderFormV2) {
      navigate(buildOrderInfoPath(args.orderId));
      return;
    }
    if (args && args?.orderId) {
      resetStore();
      resetCombinedStore();
      updateCombinedWorkflowProducts(args?.productTypes ?? []);
      if (
        (args?.productTypes?.[0] === ItemType.PARTIAL_DENTURE &&
          NEW_PAP_WORKFLOW_ACTIVATION_DATE.getTime() <= new Date().getTime()) ||
        args?.productTypes?.[0] === ItemType.FULL_DENTURE
      ) {
        navigate("/create-new-order", {
          state: {
            id: args?.orderId,
            workflow: args?.productTypes ?? [],
            cameFromOtherProduct: false,
          },
        });
      } else {
        navigate("/create-order", {
          state: {
            id: args?.orderId,
          },
        });
      }
    } else {
      console.error("Err argument websocket", args);
    }
  };

  const openNewOrderRef = React.useRef(openNewOrder);
  openNewOrderRef.current = openNewOrder;

  const userId = isLoggedIn ? user?.id : undefined;

  React.useEffect(() => {
    if (!userId) {
      return;
    }

    const socket: Socket = io(getWebsocketApiUrl(), {
      query: {
        user_id: userId,
        type: "order",
      },
    });

    socket.on("connect", () => {
      socket.emit("joinRoom", { room: "user-" + userId });
    });

    socket.on("new_order", args => openNewOrderRef.current(args));

    socket.on("connect_error", (error: Error) => {
      console.log(
        `Connection failed RoutesDispatcher.tsx (webapp) #${error.message}`
      );
    });

    socket.on("disconnect", (reason: string) => {
      console.info("disconnect in RoutesDispatcher.tsx (webapp)", reason);
      if (reason === "io server disconnect") {
        socket.connect();
      }
    });

    const handleVisibilityChange = () => {
      if (!document.hidden && socket.disconnected) {
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      socket.disconnect();
    };
  }, [userId]);

  return null;
};

const LoginRequired = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn } = useAuthContext();

  return isLoggedIn ? (
    // isCreatingOrder ? (
    //   <Navigate to="/test" />
    // ) : (
    // <CreateOrderFileInterceptors>{children}</CreateOrderFileInterceptors>
    <LoggedInLayout>{children}</LoggedInLayout>
  ) : (
    // )/>
    <Navigate to="/login" />
  );
};

const CreateOrderRedirect = ({ children }: { children: JSX.Element }) => {
  const { creatingOrderId } = useIpcContext();
  const { isLoggedIn } = useAuthContext();
  return isLoggedIn &&
    creatingOrderId !== null &&
    typeof creatingOrderId === "string" ? (
    <Navigate to="/create-order" state={{ id: creatingOrderId }} />
  ) : (
    <CreateOrderFileInterceptors>{children}</CreateOrderFileInterceptors>
  );
};

const LoginRedirect = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn } = useAuthContext();
  return isLoggedIn ? <Navigate to="/" /> : children;
};
